import { RadioGroup } from "@headlessui/react";
import {
  Alert,
  cn,
  Flex,
  Input,
  Loading,
  Select,
  Switcher,
  Typography,
} from "djuno-design";
import {
  Control,
  Controller,
  FieldErrors,
  useController,
} from "react-hook-form";
import { useAppSelector } from "../../../hooks";
import { AiAppDeployType, AiAppScallingStrategy } from "../../../types/ai";
import {
  selectAiAppImages,
  selectAiAppImagesLoading,
} from "../../../store/ai/deploys/appsSlice";
import { NumberStepper } from "../../general/NumberStepper";
import RadioGrouper, { RadioGroupeItem } from "../../general/RadioGrouper";
import { DBSCatalog } from "../../../types/database";
import { currency } from "../../../utils/currency";

export const applicationDeployTypes: Array<{
  value: AiAppDeployType;
  title: string;
  description: string;
}> = [
  {
    value: "docker-image",
    title: "Use your own Docker image",
    description:
      "Easily deploy your own application from a custom Docker image",
  },
  {
    value: "partner",
    title: "Explore our catalogue of ready-to-use apps",
    description:
      "Easily deploy an application of your choice from our partner models",
  },
];

export const AiAppDeployTypesInput = ({
  control,
  selectedDeployType,
  setSelectedDeployType,
  catalogs,
  catalogsLoading,
  errors,
  loading,
  disabled,
}: {
  control: Control<any, any>;
  selectedDeployType: AiAppDeployType;
  setSelectedDeployType: (useCase: AiAppDeployType) => void;
  catalogs?: DBSCatalog[];
  catalogsLoading?: boolean;
  errors?: FieldErrors<any>;
  loading?: boolean;
  disabled?: boolean;
}) => {
  const images = useAppSelector(selectAiAppImages);
  const imagesLoading = useAppSelector(selectAiAppImagesLoading);

  // const partners = useAppSelector(selectAiAppPartners);
  // const partnersLoading = useAppSelector(selectAiAppPartnersLoading);

  const {
    field: { value: version, onChange: onChangeVersion },
  } = useController({ name: "selectedImageTag", control });

  if (loading) {
    return (
      <Flex justify="center" items="center" className="w-full !min-h-[100px]">
        <Loading borderSize={2} />
      </Flex>
    );
  }

  return (
    <Flex direction="col" className="gap-4">
      {/* Deploy type */}
      <Flex direction="col">
        <RadioGroup
          value={selectedDeployType || null}
          disabled={disabled}
          onChange={(v) => setSelectedDeployType(v || undefined)}
        >
          <div className="flex flex-col w-full mt-3">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mb-5">
              {applicationDeployTypes.map((deployType, i) => (
                <RadioGroup.Option key={i} value={deployType.value}>
                  {({ checked, disabled }) => {
                    return (
                      <div
                        className={cn(
                          "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                          {
                            "border-primary-400 dark:border-primary-400":
                              checked,
                            "!cursor-not-allowed": disabled,
                          }
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Typography.Text className="!text-sm">
                            {deployType.title}
                          </Typography.Text>
                        </div>
                        <div className="flex flex-col border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                          <Typography.Text
                            uiType="secondary"
                            className="!text-xs"
                          >
                            {deployType.description}
                          </Typography.Text>
                        </div>
                      </div>
                    );
                  }}
                </RadioGroup.Option>
              ))}
            </div>
          </div>
        </RadioGroup>
      </Flex>

      {/* images */}
      <Flex direction="col">
        {selectedDeployType === "docker-image" && (
          <Flex direction="col" className="w-full gap-5">
            <Typography.Text className="!text-sm">
              Deploy your Docker image by entering its path below. It can come
              from a public registry (Github, Gitlab, Dockerhub...) or private
              registry.
            </Typography.Text>
            <Alert uiType="info" showIcon>
              <ul className="list-disc marker:text-primary-400 pl-8 space-y-1 list-image-[#eee]">
                <li>
                  <Typography.Text className="!text-xs">
                    If your Docker image is in a private registry, be sure to
                    add it as an authorised registry beforehand.
                  </Typography.Text>
                </li>
                <li>
                  <Typography.Text className="!text-xs">
                    Please note that your image must be built with the Docker
                    platform{" "}
                    <Typography.Text className="!text-xs font-semibold">
                      ‘linux/AMD64’
                    </Typography.Text>
                    . Docker-compose cannot be used.
                  </Typography.Text>
                </li>
                <li>
                  <Typography.Text className="!text-xs">
                    Make sure that the OVHcloud user (42420:42420) has the
                    necessary permissions to launch the image process.
                  </Typography.Text>
                </li>
              </ul>
              <Typography.Text className="!text-sm"></Typography.Text>
            </Alert>
            <Controller
              control={control}
              name="dockerImage"
              render={({ field: { value, onChange } }) => (
                <Input
                  label="Your Docker image path"
                  value={value}
                  onChange={onChange}
                  placeholder="ai-deploy-hello-world:latest"
                  error={errors?.dockerImage?.message as string | undefined}
                  tooltip={{
                    content:
                      "Images hosted on a private or public registry are accepted (GitHub, GitLab, DockerHub, etc.)",
                    className: "!text-xs",
                  }}
                />
              )}
            />

            <Controller
              control={control}
              name="dockerRunCommand"
              render={({ field: { value, onChange } }) => (
                <Input
                  label="Specify the command to run (optional)"
                  value={value}
                  onChange={onChange}
                  error={
                    errors?.dockerRunCommand?.message as string | undefined
                  }
                />
              )}
            />
          </Flex>
        )}

        {selectedDeployType === "partner" && (
          <>
            <Typography.Text className="!text-sm">
              Choose from the following catalogue of applications to speed up
              the delivery of your services:
            </Typography.Text>
            {imagesLoading && (
              <Flex
                justify="center"
                items="center"
                className="w-full !min-h-[100px]"
              >
                <Loading borderSize={2} />
              </Flex>
            )}
            {!imagesLoading && (
              <Controller
                name={"selectedImage"}
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <>
                      <RadioGroup
                        value={value || null}
                        disabled={disabled}
                        onChange={(imageId: string) => {
                          const _selectedImage = images.find(
                            (i) => i.id === imageId
                          );
                          if (_selectedImage) {
                            onChange(_selectedImage.id);
                            onChangeVersion(_selectedImage.versions[0]);
                          }
                        }}
                      >
                        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 my-5">
                          {images.map((image, i) => (
                            <RadioGroup.Option key={i} value={image.id}>
                              {({ checked, disabled }) => {
                                const cpuCatalog = catalogs?.find((c) => {
                                  return image.partnerId === "voxist"
                                    ? c.planCode.match(
                                        image.id +
                                          "-cpu-bracket1.unit.consumption"
                                      )
                                    : c.planCode.match(
                                        image.partnerId +
                                          "-" +
                                          image.id +
                                          "-cpu.minute.consumption"
                                      );
                                });

                                const gpuCatalog = catalogs?.find((c) => {
                                  return image.partnerId === "voxist"
                                    ? c.planCode.match(
                                        image.id +
                                          "-gpu-bracket1.unit.consumption"
                                      )
                                    : c.planCode.match(
                                        image.partnerId +
                                          "-" +
                                          image.id +
                                          "-gpu.minute.consumption"
                                      );
                                });
                                return (
                                  <div
                                    className={cn(
                                      "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                                      {
                                        "border-primary-400 dark:border-primary-400":
                                          checked,
                                        "!cursor-not-allowed": disabled,
                                      }
                                    )}
                                  >
                                    <Flex
                                      justify="between"
                                      items="center"
                                      className="gap-1 mb-2 w-full"
                                    >
                                      <Flex direction="col" className="gap-1">
                                        <Typography.Text className="!text-sm !font-semibold">
                                          {image.name}
                                        </Typography.Text>

                                        <Typography.Text className="!text-xs">
                                          {image.partnerId}
                                        </Typography.Text>
                                      </Flex>
                                      <img
                                        alt=""
                                        src={image.logoUrl}
                                        className="h-10"
                                      />
                                    </Flex>
                                    <div className="flex items-center border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                      {image.versions.length > 1 ? (
                                        <Select
                                          className="!w-full"
                                          optionsClassName="!max-h-[250px]"
                                          options={[
                                            ...image.versions.map((v) => ({
                                              label: v,
                                              value: v,
                                            })),
                                          ]}
                                          value={
                                            value === image.id
                                              ? version
                                              : image.versions[0]
                                          }
                                          onChange={onChangeVersion}
                                        />
                                      ) : (
                                        <Flex items="center" className="h-9">
                                          <Typography.Text
                                            size="sm"
                                            uiType="secondary"
                                          >
                                            {image.versions[0]}
                                          </Typography.Text>
                                        </Flex>
                                      )}
                                    </div>
                                    <div className="flex flex-col border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                      <Typography.Text
                                        className="!text-xs mt-1"
                                        uiType="secondary"
                                      >
                                        {image.description}
                                      </Typography.Text>
                                    </div>
                                    <div className="flex flex-col border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                      {catalogsLoading && (
                                        <Loading borderSize={2} uiSize={14} />
                                      )}
                                      {cpuCatalog && !catalogsLoading && (
                                        <Typography.Text
                                          uiType="secondary"
                                          className="!text-xs"
                                        >
                                          Licence price on CPU: from{" "}
                                          {currency(
                                            cpuCatalog.pricings[0].price *
                                              60 *
                                              (image.partnerId === "voxist"
                                                ? 60
                                                : 1),
                                            { minimumFractionDigits: 3 }
                                          )}{" "}
                                          per resource/hour
                                        </Typography.Text>
                                      )}
                                      {gpuCatalog && !catalogsLoading && (
                                        <Typography.Text
                                          uiType="secondary"
                                          className="!text-xs"
                                        >
                                          Licence price on GPU: from{" "}
                                          {currency(
                                            gpuCatalog.pricings[0].price *
                                              60 *
                                              (image.partnerId === "voxist"
                                                ? 60
                                                : 1),
                                            { minimumFractionDigits: 3 }
                                          )}{" "}
                                          per resource/hour
                                        </Typography.Text>
                                      )}
                                    </div>
                                  </div>
                                );
                              }}
                            </RadioGroup.Option>
                          ))}
                        </div>
                      </RadioGroup>
                    </>
                  );
                }}
              />
            )}
          </>
        )}
        {/* <AnimatedFormError error={errorMessage} /> */}
      </Flex>
    </Flex>
  );
};

export const AiAppScallingStrategyInput = ({
  control,
  scallingStrategy,
  setScallingStrategy,
  errors,
  loading,
}: {
  control: Control<any, any>;
  scallingStrategy: AiAppScallingStrategy;
  setScallingStrategy: (strategy: AiAppScallingStrategy) => void;
  errors?: FieldErrors<any>;
  loading?: boolean;
}) => {
  const resourceTypes: RadioGroupeItem[] = [
    { label: "CPU", value: "CPU" },
    { label: "RAM", value: "RAM" },
  ];

  if (loading) {
    return (
      <Flex justify="center" items="center" className="w-full !min-h-[100px]">
        <Loading borderSize={2} />
      </Flex>
    );
  }

  return (
    <Flex direction="col" className="gap-5">
      <Flex direction="col" className="gap-1">
        <Typography.Text size="sm" className="!font-semibold">
          Scaling
        </Typography.Text>
        <Typography.Text size="sm">
          Pick the deployment strategy that best suits your app. Choose a fixed
          size for the number of replicas your app will be deployed on, or
          specify a minimum and maximum number of replicas for your deployments.
          This will dynamically optimise performance, and adapt to the number of
          requests via the auto-scaling option.
        </Typography.Text>
        <Alert uiType="info" showIcon>
          <Typography.Text size="xs">
            Please note that the number of replica nodes you choose will
            increase the number of resources you use.
          </Typography.Text>
        </Alert>
      </Flex>
      <Flex direction="col" className="gap-1">
        <Typography.Text size="sm">Strategy</Typography.Text>
        <Flex items="center" className="gap-4">
          <Switcher
            value={scallingStrategy === "automatic"}
            onChange={(v) => setScallingStrategy(v ? "automatic" : "fixed")}
          />
          <Typography.Text size="sm">
            Auto-scaling{" "}
            {scallingStrategy === "automatic" ? "enabled" : "disabled"}
          </Typography.Text>
        </Flex>
      </Flex>
      {scallingStrategy === "fixed" && (
        <Flex direction="col">
          <Typography.Text size="sm">
            Number of replicas your app will be deployed on
          </Typography.Text>
          <Controller
            control={control}
            name="replicas"
            render={({ field: { value, onChange } }) => (
              <NumberStepper
                value={value}
                min={1}
                max={10}
                onChange={onChange}
                error={errors?.replicas?.message as undefined | string}
              />
            )}
          />
          <Typography.Text size="xs">
            Deploy on a minimum of 2 replicas for high availability
          </Typography.Text>
        </Flex>
      )}

      {scallingStrategy === "automatic" && (
        <Flex direction="col" className="gap-5">
          <Flex direction="col" className="gap-1">
            <Typography.Text size="sm">Minimum replicas</Typography.Text>
            <Controller
              control={control}
              name="replicasMin"
              render={({ field: { value, onChange } }) => (
                <NumberStepper
                  value={value}
                  min={1}
                  max={100}
                  onChange={onChange}
                  error={errors?.replicasMin?.message as undefined | string}
                />
              )}
            />
          </Flex>

          <Flex direction="col" className="gap-1">
            <Typography.Text size="sm">Maximum replicas</Typography.Text>
            <Controller
              control={control}
              name="replicasMax"
              render={({ field: { value, onChange } }) => (
                <NumberStepper
                  value={value}
                  min={1}
                  max={100}
                  onChange={onChange}
                  error={errors?.replicasMax?.message as undefined | string}
                />
              )}
            />
          </Flex>

          <Flex direction="col" className="gap-1">
            <Typography.Text size="sm">Metric monitored</Typography.Text>
            <Controller
              control={control}
              name="resourceType"
              render={({ field: { value, onChange } }) => (
                <RadioGrouper
                  items={resourceTypes}
                  selected={resourceTypes.find((r) => r.value === value)}
                  setSelected={(item) => onChange(item.value)}
                />
              )}
            />
            <Typography.Text size="xs">
              The metric that will act as the trigger for auto-scaling
            </Typography.Text>
          </Flex>

          <Flex direction="col" className="gap-1">
            <Typography.Text size="sm">Trigger threshold (%)</Typography.Text>
            <Controller
              control={control}
              name="averageUsageTarget"
              render={({ field: { value, onChange } }) => (
                <NumberStepper
                  value={value}
                  min={1}
                  max={100}
                  onChange={onChange}
                  error={
                    errors?.averageUsageTarget?.message as undefined | string
                  }
                />
              )}
            />
            <Typography.Text size="xs">
              The metric value, in percent.
            </Typography.Text>
          </Flex>

          <Alert uiType="info" showIcon>
            <Typography.Text size="xs">
              The total price of the deployment is calculated based on the
              minimum number of replicas selected. Please note that this cost
              may increase when auto-scaling is enabled.
            </Typography.Text>
          </Alert>
        </Flex>
      )}
    </Flex>
  );
};

export const AiAppReadinessProbeInput = ({
  control,
  readinessProbe,
  setReadinessProbe,
  errors,
  loading,
}: {
  control: Control<any, any>;
  readinessProbe: boolean;
  setReadinessProbe: (type: boolean) => void;
  errors?: FieldErrors<any>;
  loading?: boolean;
}) => {
  if (loading) {
    return (
      <Flex justify="center" items="center" className="w-full !min-h-[100px]">
        <Loading borderSize={2} />
      </Flex>
    );
  }

  return (
    <Flex direction="col" className="gap-5">
      <Flex direction="col" className="gap-1">
        <Typography.Text size="sm" className="!font-semibold">
          Readiness probe
        </Typography.Text>
        <Typography.Text size="sm">
          To determine when your application is ready to receive a HTTP request,
          you can configure a fully-customised readiness probe (Kubernetes
          Readiness probe) by adding an API endpoint and port.
        </Typography.Text>
      </Flex>
      <Flex direction="col" className="gap-1">
        <Typography.Text size="sm">Use a readiness probe</Typography.Text>
        <Flex items="center" className="gap-4">
          <Switcher value={readinessProbe} onChange={setReadinessProbe} />
          <Typography.Text size="sm">
            Readiness probe {readinessProbe ? "enabled" : "disabled"}
          </Typography.Text>
        </Flex>
      </Flex>
      {readinessProbe && (
        <Flex direction="col" className="gap-5">
          <Flex direction="col" className="gap-1">
            <Controller
              control={control}
              name="readinessProbePath"
              render={({ field: { value, onChange } }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  placeholder="/health"
                  error={
                    errors?.readinessProbePath?.message as undefined | string
                  }
                />
              )}
            />
            <Typography.Text size="xs">
              This is the API endpoint for the readiness probe. Must return a
              2XX or 3XX status
            </Typography.Text>
          </Flex>

          <Flex direction="col" className="gap-1">
            <Controller
              control={control}
              name="readinessProbePort"
              render={({ field: { value, onChange } }) => (
                <Input
                  value={value}
                  onChange={onChange}
                  error={
                    errors?.readinessProbePort?.message as undefined | string
                  }
                  type="number"
                />
              )}
            />
            <Typography.Text size="xs">
              Enables you to configure a specific port for this API access path
            </Typography.Text>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
